import React, { useState } from "react";
import { X, Plus, Trash2, Save, Mail, Phone, MapPin, Star, Clock, StickyNote, PhoneCall, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);
const ACT_ICONS = { note: StickyNote, call: PhoneCall, email: Mail, meeting: Users2 };

export default function ContactModal({ businessId, contact, activities = [], onSaved, onActivitiesChange, onClose }) {
  const isNew = !contact?.id;
  const [c, setC] = useState(() => ({
    name: contact?.name || "",
    title: contact?.title || "",
    emails: (contact?.emails?.length ? contact.emails : [{ label: "Work", value: "" }]).map((e) => ({ ...e, _k: uid() })),
    phones: (contact?.phones?.length ? contact.phones : [{ label: "Mobile", value: "" }]).map((p) => ({ ...p, _k: uid() })),
    address: contact?.address || "",
    notes: contact?.notes || "",
    is_primary: contact?.is_primary || false,
  }));
  const [saving, setSaving] = useState(false);
  const [acts, setActs] = useState(activities);
  const [actForm, setActForm] = useState({ type: "note", text: "" });

  const set = (k, v) => setC((s) => ({ ...s, [k]: v }));
  const updList = (key, k2, i, val) => set(key, c[key].map((x, idx) => (idx === i ? { ...x, [k2]: val } : x)));
  const addRow = (key, label) => set(key, [...c[key], { label, value: "", _k: uid() }]);
  const delRow = (key, i) => set(key, c[key].filter((_, idx) => idx !== i));

  const save = async () => {
    if (!c.name.trim()) { toast.error("Contact name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: c.name, title: c.title, address: c.address, notes: c.notes, is_primary: c.is_primary,
        emails: c.emails.filter((e) => e.value.trim()).map((e) => ({ label: e.label || "", value: e.value })),
        phones: c.phones.filter((p) => p.value.trim()).map((p) => ({ label: p.label || "", value: p.value })),
      };
      const saved = isNew ? await api.adminAddContact(businessId, payload) : await api.adminUpdateContact(businessId, contact.id, payload);
      toast.success(isNew ? "Contact added" : "Contact updated");
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save contact");
    } finally {
      setSaving(false);
    }
  };

  const addActivity = async () => {
    if (!actForm.text.trim()) { toast.error("Add a note"); return; }
    const a = await api.adminAddActivity(businessId, { ...actForm, contact_id: contact?.id || null });
    const next = [a, ...acts];
    setActs(next);
    onActivitiesChange?.(next);
    setActForm({ type: "note", text: "" });
    toast.success("Logged to contact history");
  };

  const delActivity = async (aid) => {
    await api.adminDeleteActivity(businessId, aid);
    const next = acts.filter((a) => a.id !== aid);
    setActs(next);
    onActivitiesChange?.(next);
  };

  const delContact = async () => {
    if (!contact?.id) return;
    if (!window.confirm(`Remove ${contact.name} from this business?`)) return;
    try {
      await api.adminDeleteContact(businessId, contact.id);
      toast.success("Contact removed");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove contact");
    }
  };

  const fmt = (s) => { try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return s; } };
  const input = "w-full px-3 py-2.5 rounded-lg dark-input text-sm";
  const label = "block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="contact-modal">
      <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-3xl border border-white/10 fade-in overflow-hidden flex flex-col" style={{ maxHeight: "92vh" }}>
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-primary-500/15 text-primary-300 flex items-center justify-center font-bold text-sm">{(c.name || "?").slice(0, 1).toUpperCase()}</div>
            <div>
              <h3 className="text-base font-bold text-white leading-tight">{c.name || "New Contact"}</h3>
              {c.title && <p className="text-xs text-zinc-400">{c.title}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="contact-modal-close"><X /></button>
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar grid grid-cols-1 md:grid-cols-5">
          {/* Details */}
          <div className="md:col-span-3 p-6 space-y-4 border-b md:border-b-0 md:border-r border-white/10">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Full Name *</label><input value={c.name} onChange={(e) => set("name", e.target.value)} className={input} data-testid="contact-name" /></div>
              <div><label className={label}>Title / Role</label><input value={c.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Founder" className={input} data-testid="contact-title" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5"><label className={label} style={{ margin: 0 }}><Mail size={11} className="inline mr-1" /> Emails</label><button onClick={() => addRow("emails", "Other")} className="text-primary-400 text-xs font-bold flex items-center hover:text-primary-300"><Plus size={12} /> Add</button></div>
              {c.emails.map((e, i) => (
                <div key={e._k} className="flex gap-2 mb-2">
                  <input value={e.label} onChange={(ev) => updList("emails", "label", i, ev.target.value)} placeholder="Label" className={`${input} w-24`} />
                  <input value={e.value} onChange={(ev) => updList("emails", "value", i, ev.target.value)} placeholder="email@company.com" className={input} data-testid="contact-email" />
                  <button onClick={() => delRow("emails", i)} className="text-zinc-500 hover:text-red-400 px-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5"><label className={label} style={{ margin: 0 }}><Phone size={11} className="inline mr-1" /> Phone Numbers</label><button onClick={() => addRow("phones", "Other")} className="text-primary-400 text-xs font-bold flex items-center hover:text-primary-300"><Plus size={12} /> Add</button></div>
              {c.phones.map((p, i) => (
                <div key={p._k} className="flex gap-2 mb-2">
                  <input value={p.label} onChange={(ev) => updList("phones", "label", i, ev.target.value)} placeholder="Label" className={`${input} w-24`} />
                  <input value={p.value} onChange={(ev) => updList("phones", "value", i, ev.target.value)} placeholder="(555) 123-4567" className={input} data-testid="contact-phone" />
                  <button onClick={() => delRow("phones", i)} className="text-zinc-500 hover:text-red-400 px-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>

            <div><label className={label}><MapPin size={11} className="inline mr-1" /> Address</label><input value={c.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, City, State" className={input} data-testid="contact-address" /></div>
            <div><label className={label}>Notes</label><textarea value={c.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Anything useful to remember…" className={input} data-testid="contact-notes" /></div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer"><input type="checkbox" checked={c.is_primary} onChange={(e) => set("is_primary", e.target.checked)} className="accent-primary-500" data-testid="contact-primary" /><Star size={13} className="text-amber-400" /> Primary contact</label>
          </div>

          {/* Contact history */}
          <div className="md:col-span-2 p-6 space-y-3 bg-black/20">
            <h4 className="text-sm font-bold text-white flex items-center gap-2"><Clock size={14} className="text-primary-400" /> Contact History</h4>
            {!isNew ? (
              <>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <select value={actForm.type} onChange={(e) => setActForm({ ...actForm, type: e.target.value })} className="px-2 py-2 rounded-lg dark-input text-xs" data-testid="activity-type">
                      <option value="note">Note</option><option value="call">Call</option><option value="email">Email</option><option value="meeting">Meeting</option>
                    </select>
                    <input value={actForm.text} onChange={(e) => setActForm({ ...actForm, text: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addActivity()} placeholder="Log an interaction…" className="flex-1 px-3 py-2 rounded-lg dark-input text-sm" data-testid="activity-text" />
                    <button onClick={addActivity} className="px-3 rounded-lg bg-primary-600 text-white hover:bg-primary-500" data-testid="activity-add"><Plus size={15} /></button>
                  </div>
                </div>
                <div className="space-y-2 max-h-[46vh] overflow-y-auto hide-scrollbar">
                  {acts.length === 0 && <p className="text-zinc-600 text-xs py-4 text-center">No history yet. Log your first interaction.</p>}
                  {acts.map((a) => {
                    const Icon = ACT_ICONS[a.type] || StickyNote;
                    return (
                      <div key={a.id} className="p-3 rounded-xl bg-white/5 border border-white/10 group" data-testid="activity-item">
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded-full bg-primary-500/15 text-primary-300 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon size={12} /></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-zinc-200 break-words">{a.text}</p>
                            <p className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wide">{a.type} · {fmt(a.at)}</p>
                          </div>
                          <button onClick={() => delActivity(a.id)} className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-zinc-600 text-xs">Save this contact first to start logging call, email and meeting history.</p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex justify-between items-center gap-3 bg-white/5">
          <div>
            {!isNew && <button onClick={delContact} className="px-4 py-2.5 rounded-xl bg-red-500/10 text-red-400 text-sm font-bold hover:bg-red-500/20 flex items-center" data-testid="contact-delete-btn"><Trash2 size={14} className="mr-1.5" /> Delete</button>}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10">Close</button>
            <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 flex items-center disabled:opacity-60" data-testid="contact-save-btn"><Save size={15} className="mr-2" /> {saving ? "Saving…" : isNew ? "Add Contact" : "Save Contact"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
